import { apiGet, planningPath } from './PlanningApiClient';

export type ActionActorType = 'user' | 'ai' | 'guest' | 'service_account' | 'system';

export interface ActionLogEntry {
  /** uuid, kept as text. */
  id: string;
  actorType: ActionActorType;
  actorEmail: string | null;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  /** The API no longer stores a summary; screens build their wording from action + entity. */
  summary: string | null;
  createdAt: string;
}

interface ApiActionLogEntry {
  id: string | number;
  actor_type?: ActionActorType;
  actor_email: string | null;
  actor_name?: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  summary: string | null;
  created_at: string;
}

function transform(row: ApiActionLogEntry): ActionLogEntry {
  return {
    id: String(row.id),
    actorType: row.actor_type ?? 'user',
    actorEmail: row.actor_email,
    actorName: row.actor_name ?? null,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    summary: row.summary,
    createdAt: row.created_at,
  };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** "budget item", or "task #42" for a short legacy id. A uuid is never shown. */
export function describeEntity(entry: Pick<ActionLogEntry, 'entityType' | 'entityId'>): string {
  const name = entry.entityType.replace(/_/g, ' ');
  return entry.entityId && !UUID.test(entry.entityId) ? `${name} #${entry.entityId}` : name;
}

/** Who did it, in words. */
export function describeActor(entry: Pick<ActionLogEntry, 'actorName' | 'actorEmail' | 'actorType'>): string {
  if (entry.actorName) return entry.actorName;
  if (entry.actorEmail) return entry.actorEmail;
  return entry.actorType === 'user' ? 'Unknown' : entry.actorType === 'system' ? 'System' : entry.actorType.replace(/_/g, ' ');
}

export async function loadActionLog(limit = 50): Promise<ActionLogEntry[]> {
  const response = await apiGet<{ entries: ApiActionLogEntry[] }>(planningPath(`/action-log?limit=${limit}`));
  return (response.entries || []).map(transform);
}
